import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useV3Shell } from '../v3/V3ShellContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Package, ClipboardList, Home, FileQuestion, ChevronDown, Plus, Upload, TrendingUp, UserPlus, X, Clock, Phone, ShoppingCart, Truck, BarChart2, PackageCheck, Link2, ExternalLink, Shield, Users2, LineChart, Boxes, Ban, Loader2, Calculator } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';
import Quotes from '../pages/Quotes';
import Proforma from '../pages/Proforma';
import ToolsDropdown from './ToolsDropdown';
import AddCallToCrmModal from './AddCallToCrmModal';
import LogCallDropdownPill from './LogCallDropdownPill';
import CallNotificationBar from './CallNotificationBar';
import NotificationBars from './NotificationBars';
import QuickLinksRow from './QuickLinksRow';
import QuickCallNoteButton from './QuickCallNoteButton';
import { StockRequestModal } from './StockRequestModal';
import GlobalUploadModal from './GlobalUploadModal';
import HOSupportCalculatorModal from './HOSupportCalculatorModal';
import { useZohoSession } from '../contexts/ZohoSessionContext';
import B29StockTransfer from '../pages/B29StockTransfer';
import { useBranch } from '../context/BranchContext';
import { useNavSectors } from '../hooks/useNavSectors';
import { ICON_MAP, PAGE_REGISTRY, BUILTIN_ACTIONS, actionVisible } from '../utils/navHeaderRegistry';
import { useTransportReturn } from '../contexts/TransportReturnContext';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useDashboardV3 } from '../contexts/DashboardV3Context';
import { useNavLinkStyles, linkStyleKey, linkColorCss, linkActiveCss, linkOpenCss, HOST_WINDOW_LINKS, TRANSPORT_LINK, CLASSIC_LINKS } from '../utils/navLinkStyles';
import { useActivityBar } from '../contexts/ActivityBarContext';

const enconOrange = '#ee7203';

// Inside UI V3 a window is drawn on the page body, not inside the side bar that opened it. Inside the bar it was stacked and sized by the bar, so Pro Forma, Quotes
// and the stock windows sat off-centre and short while Customer Management (already drawn on the body) was right. On the body every window behaves the same way.
// Outside V3 nothing changes: the window is drawn exactly where it always was.
const InBody = ({ children }) => {
  const shell = useV3Shell();
  return shell ? createPortal(children, document.body) : children;
};
const ProformaModalInBody = (props) => <InBody><Proforma.Modal {...props} /></InBody>;
const QuotesModalInBody = (props) => <InBody><Quotes.Modal {...props} /></InBody>;
const enconPurple = '#9333ea';

// Dropdown Pill Component for Quote/ProForma
export const DropdownPill = ({ label, icon: Icon, permissionKey, isQuote, user, shape = 'pill' }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const dropdownRef = useRef(null);
  const cornerClass = shape === 'oblong' ? 'rounded-xl' : 'rounded-full';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openModal = (mode) => {
    setModalMode(mode);
    setShowDropdown(false);
  };

  // Get the appropriate modal component (drawn on the page body inside UI V3, see InBody)
  const ModalComponent = isQuote ? { Modal: QuotesModalInBody } : { Modal: ProformaModalInBody };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          data-testid={`nav-pill-${isQuote ? 'quotes' : 'proforma'}-dropdown`}
          data-v3-open={modalMode !== null ? 'true' : undefined}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }}
          className={`h-9 flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 ${cornerClass} border-2 bg-white hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap ${showDropdown ? 'shadow-lg' : ''}`}
          style={{ 
            borderColor: enconOrange,
            position: 'relative',
            zIndex: showDropdown ? 51 : 'auto'
          }}
        >
          <Icon className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: enconOrange }} />
          <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: enconOrange }}>
            {label}
          </span>
          <ChevronDown 
            className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} 
            style={{ color: enconOrange }} 
          />
        </button>

        {showDropdown && (
          <div 
            className="absolute left-1/2 -translate-x-1/2 mt-1 w-48 rounded-2xl shadow-xl overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(45, 45, 45, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
              border: `2px solid ${enconOrange}`,
              backdropFilter: 'blur(10px)',
              top: '100%',
              zIndex: 9999
            }}
          >
            {/* Arrow pointer connecting to pill */}
            <div 
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
              style={{ 
                background: 'rgba(45, 45, 45, 0.98)',
                borderLeft: `2px solid ${enconOrange}`,
                borderTop: `2px solid ${enconOrange}`
              }}
            />
            <div className="relative">
              <button
                data-testid={`nav-pill-${isQuote ? 'quotes' : 'proforma'}-create`}
                onClick={() => openModal('create')}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl"
              >
                <Plus className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">Create New</span>
              </button>
              {isQuote && (
                <button
                  data-testid="nav-pill-quotes-recent"
                  onClick={() => openModal('recent')}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                >
                  <Clock className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">Recent Quotes</span>
                </button>
              )}
              {!isQuote && (
                <button
                  data-testid="nav-pill-proforma-recent"
                  onClick={() => openModal('recent')}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                >
                  <Clock className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">Recent Pro Formas</span>
                </button>
              )}
              <button
                data-testid={`nav-pill-${isQuote ? 'quotes' : 'proforma'}-upload`}
                onClick={() => openModal('upload')}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl"
              >
                <Upload className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">{isQuote ? 'Upload PDF' : 'Upload from PDF'}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal rendered directly - cannot be closed by outside click */}
      {modalMode !== null && (
        <ModalComponent.Modal 
          isOpen={true} 
          onClose={() => setModalMode(null)} 
          mode={modalMode}
          user={user}
        />
      )}
    </>
  );
};

// Stock Transfers / Requests Combined Dropdown Pill
export const StockTransfersRequestsPill = ({ user, shape = 'pill', icon: TriggerIcon = Package }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showB29Modal, setShowB29Modal] = useState(false);
  const dropdownRef = useRef(null);
  const cornerClass = shape === 'oblong' ? 'rounded-xl' : 'rounded-full';

  const canAccessTransfer = !user || user.role === 'super_admin' || user.can_access_stock_transfer !== false;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          data-testid="nav-pill-stock-transfers-requests"
          data-v3-open={showRequestModal || showB29Modal ? 'true' : undefined}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDropdown(!showDropdown); }}
          className={`h-9 flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 ${cornerClass} border-2 bg-white hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap ${showDropdown ? 'shadow-lg' : ''}`}
          style={{ borderColor: enconOrange, position: 'relative', zIndex: showDropdown ? 51 : 'auto' }}
        >
          <TriggerIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: enconOrange }} />
          <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: enconOrange }}>
            Stock Trf/Req
          </span>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
            style={{ color: enconOrange }}
          />
        </button>

        {showDropdown && (
          <div
            className="absolute left-1/2 -translate-x-1/2 mt-1 w-56 rounded-2xl shadow-xl overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
              border: `2px solid ${enconOrange}`,
              backdropFilter: 'blur(10px)',
              top: '100%',
              zIndex: 9999
            }}
          >
            <div
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
              style={{ background: 'rgba(45,45,45,0.98)', borderLeft: `2px solid ${enconOrange}`, borderTop: `2px solid ${enconOrange}` }}
            />
            <div className="relative">
              {canAccessTransfer && (
                <button
                  data-testid="nav-pill-stock-transfers-link"
                  onClick={() => { setShowDropdown(false); navigate('/stock-transfer'); }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl"
                >
                  <Package className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">Stock Transfers</span>
                </button>
              )}
              {canAccessTransfer && (
                <button
                  data-testid="nav-pill-b29-transfers-link"
                  onClick={() => { setShowDropdown(false); setShowB29Modal(true); }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors"
                >
                  <Boxes className="w-4 h-4" style={{ color: '#fbbf24' }} />
                  <span className="font-medium">B29 Transfer</span>
                </button>
              )}
              <div className="border-t border-white/25 mx-3 my-0.5" />
              <button
                data-testid="nav-pill-stock-request-manual"
                onClick={() => { setShowDropdown(false); setShowRequestModal(true); }}
                className={`w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors ${canAccessTransfer ? 'rounded-b-xl' : 'rounded-xl'}`}
              >
                <ShoppingCart className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">Stock Request</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <InBody>
        <StockRequestModal
          isOpen={showRequestModal}
          onClose={() => setShowRequestModal(false)}
        />

        <B29StockTransfer.Modal
          isOpen={showB29Modal}
          onClose={() => setShowB29Modal(false)}
          user={user}
        />
      </InBody>
    </>
  );
};

// ─── Stock Management dropdown — same 3 options as the Home page card ────────
const STOCK_MGMT_VIEWS = [
  { id: 'stock-ordering', label: 'Stock Ordering', icon: Package },
  { id: 'stock-efficiency', label: 'Stock Efficiency', icon: TrendingUp },
  { id: 'stocktake', label: 'Stocktake', icon: ClipboardList },
];

export const StockManagementDropdownPill = ({ user, shape = 'pill', icon: TriggerIcon = Boxes }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const cornerClass = shape === 'oblong' ? 'rounded-xl' : 'rounded-full';
  const canAccess = user?.role === 'super_admin' || user?.can_access_stock_management;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!canAccess) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="nav-pill-stock-management"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDropdown(!showDropdown); }}
        className={`h-9 flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 ${cornerClass} border-2 bg-white hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap ${showDropdown ? 'shadow-lg' : ''}`}
        style={{ borderColor: enconOrange, position: 'relative', zIndex: showDropdown ? 51 : 'auto' }}
      >
        <TriggerIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: enconOrange }} />
        <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: enconOrange }}>Stock Control</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
      </button>

      {showDropdown && (
        <div
          className="absolute left-1/2 -translate-x-1/2 mt-1 w-52 rounded-2xl shadow-xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
            border: `2px solid ${enconOrange}`,
            top: '100%',
            zIndex: 9999,
          }}
          data-testid="nav-pill-stock-management-menu"
        >
          {STOCK_MGMT_VIEWS.map((v, idx) => {
            const VIcon = v.icon;
            return (
              <button
                key={v.id}
                data-testid={`nav-pill-stock-management-option-${v.id}`}
                onClick={() => { setShowDropdown(false); navigate(`/stock-management?view=${v.id}`); }}
                className={`w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 ${idx === 0 ? 'rounded-t-xl border-t-0' : ''} ${idx === STOCK_MGMT_VIEWS.length - 1 ? 'rounded-b-xl' : ''}`}
              >
                <VIcon className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">{v.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Nav UI v2 — Sector dropdown + custom link/dropdown pills ─────────────────
// Trigger styled distinctly from every other header pill per spec: orange
// gradient fill, white text (vs. the white-fill/orange-border pills
// used everywhere else). Sized to match the other header pills exactly (h-9,
// border-2) — no white outline, so the gradient fill itself carries the
// distinct look without an extra visual ring bulking it out.
// A Space is a view of the dashboard, so its button and its list read "<name> View" (Default View, Sales View, Admin View). A name that already ends in View is left as it is.
export const viewLabel = (name) => {
  const text = (name || '').trim();
  if (!text) return 'View';
  return /\bview$/i.test(text) ? text : `${text} View`;
};

export const SectorDropdown = ({ sectors, activeSectorId, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  if (!sectors.length) return null;
  const active = sectors.find(s => s.id === activeSectorId);
  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        data-testid="nav-header-sector-dropdown-trigger"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        className={`h-9 flex items-center gap-1.5 px-3 rounded-xl border-2 border-transparent whitespace-nowrap hover:shadow-lg transition-all duration-200 hover:scale-105 ${open ? 'shadow-lg' : ''}`}
        style={{ background: 'linear-gradient(135deg, #ee7203 0%, #f4a35f 100%)' }}
      >
        <span className="font-semibold text-[11px] lg:text-xs text-white truncate max-w-[150px]">{viewLabel(active?.name)}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-white transition-transform duration-200 shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 mt-1 w-52 rounded-2xl shadow-xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
            border: '2px solid #ee7203',
            top: '100%',
            zIndex: 9999,
          }}
          data-testid="nav-header-sector-dropdown-menu"
        >
          {sectors.map((s, idx) => (
            <button
              key={s.id}
              data-testid={`nav-header-sector-option-${s.id}`}
              onClick={() => { onChange(s.id); setOpen(false); }}
              className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-2 hover:bg-white/15 transition-colors border-t border-white/10 ${idx === 0 ? 'rounded-t-xl border-t-0' : ''} ${idx === sectors.length - 1 ? 'rounded-b-xl' : ''}`}
            >
              <span className="text-white font-medium text-sm truncate">{viewLabel(s.name)}</span>
              {s.id === activeSectorId && <span className="text-[9px] text-orange-400 font-bold uppercase shrink-0">Active</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// A single custom/page link — plain oblong pill, internal route or external URL.
export const CustomLinkPill = ({ item, navigate }) => {
  const IconComp = ICON_MAP[item.icon] || Link2;
  const commonClass = "h-9 flex items-center gap-1.5 px-3 rounded-xl border-2 bg-white hover:shadow-md transition-all duration-200 hover:scale-105 whitespace-nowrap";
  if (item.url) {
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className={commonClass}
        style={{ borderColor: enconOrange }}
        data-testid={`nav-header-custom-link-${item.id}`}
      >
        <IconComp className="w-3.5 h-3.5" style={{ color: enconOrange }} />
        <span className="font-semibold text-[11px] lg:text-xs" style={{ color: enconOrange }}>{item.label}</span>
      </a>
    );
  }
  return (
    <button
      onClick={() => navigate(item.route || '/')}
      className={commonClass}
      style={{ borderColor: enconOrange }}
      data-testid={`nav-header-page-link-${item.id}`}
    >
      <IconComp className="w-3.5 h-3.5" style={{ color: enconOrange }} />
      <span className="font-semibold text-[11px] lg:text-xs" style={{ color: enconOrange }}>{item.label}</span>
    </button>
  );
};

// A custom dropdown built by the admin — dark panel, matches every other
// header dropdown's look; children can be internal routes or external URLs.
export const CustomDropdownPill = ({ item, navigate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const IconComp = ICON_MAP[item.icon] || Link2;
  const children = item.children || [];
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`nav-header-custom-dropdown-${item.id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        className={`h-9 flex items-center gap-1.5 px-3 rounded-xl border-2 bg-white hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap ${open ? 'shadow-lg' : ''}`}
        style={{ borderColor: enconOrange }}
      >
        <IconComp className="w-3.5 h-3.5" style={{ color: enconOrange }} />
        <span className="font-semibold text-[11px] lg:text-xs" style={{ color: enconOrange }}>{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 mt-1 w-56 rounded-2xl shadow-xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
            border: `2px solid ${enconOrange}`,
            top: '100%',
            zIndex: 9999,
          }}
        >
          {children.map((child, idx) => {
            const ChildIcon = ICON_MAP[child.icon] || Link2;
            return (
              <button
                key={child.id || idx}
                data-testid={`nav-header-custom-dropdown-child-${child.id || idx}`}
                onClick={() => {
                  setOpen(false);
                  if (child.url) window.open(child.url, '_blank', 'noopener');
                  else navigate(child.route || '/');
                }}
                className={`w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 ${idx === 0 ? 'rounded-t-xl border-t-0' : ''} ${idx === children.length - 1 ? 'rounded-b-xl' : ''}`}
              >
                <ChildIcon className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">{child.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Mobile helper sub-components ─────────────────────────────────────────────────────────────────────
// These render pill-equivalent items inside the hamburger drawer.

const MobilePillGroup = ({ label, icon: Icon, user, isQuote }) => {
  const [open, setOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const ModalComponent = isQuote ? Quotes : Proforma;
  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
        <span className="font-medium text-sm flex-1">{label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
      </button>
      {open && (
        <div className="ml-4 space-y-1">
          <button onClick={() => { setModalMode('create'); setOpen(false); }}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
            data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}-create`}
          >
            <Plus className="w-4 h-4" style={{ color: enconOrange }} />
            <span className="text-sm font-medium">Create New</span>
          </button>
          {isQuote && (
            <button onClick={() => { setModalMode('recent'); setOpen(false); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
              data-testid="mobile-nav-quotes-recent"
            >
              <Clock className="w-4 h-4" style={{ color: enconOrange }} />
              <span className="text-sm font-medium">Recent Quotes</span>
            </button>
          )}
          {!isQuote && (
            <button onClick={() => { setModalMode('recent'); setOpen(false); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
              data-testid="mobile-nav-proforma-recent"
            >
              <Clock className="w-4 h-4" style={{ color: enconOrange }} />
              <span className="text-sm font-medium">Recent Pro Formas</span>
            </button>
          )}
          <button onClick={() => { setModalMode('upload'); setOpen(false); }}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
            data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}-upload`}
          >
            <Upload className="w-4 h-4" style={{ color: enconOrange }} />
            <span className="text-sm font-medium">{isQuote ? 'Upload PDF' : 'Upload from PDF'}</span>
          </button>
        </div>
      )}
      {modalMode !== null && (
        <ModalComponent.Modal isOpen={true} onClose={() => setModalMode(null)} mode={modalMode} user={user} />
      )}
    </>
  );
};

const MobileLogCallItem = ({ user }) => {
  // Kept as no-op — replaced by LogCallDropdownPill mobileMode={true}
  return null;
};

const MobileStockItem = ({ user, navigate }) => {
  const [open, setOpen] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const canAccessTransfer = !user || user.role === 'super_admin' || user.can_access_stock_transfer !== false;
  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        data-testid="mobile-nav-stock"
      >
        <Package className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
        <span className="font-medium text-sm flex-1">Stock Trf/Req</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
      </button>
      {open && (
        <div className="ml-4 space-y-1">
          {canAccessTransfer && (
            <button onClick={() => { setOpen(false); navigate('/stock-transfer'); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
              data-testid="mobile-nav-stock-transfers"
            >
              <Package className="w-4 h-4" style={{ color: enconOrange }} />
              <span className="text-sm font-medium">Stock Transfers</span>
            </button>
          )}
          <button onClick={() => { setOpen(false); setShowRequestModal(true); }}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
            data-testid="mobile-nav-stock-request"
          >
            <ShoppingCart className="w-4 h-4" style={{ color: enconOrange }} />
            <span className="text-sm font-medium">Stock Request</span>
          </button>
        </div>
      )}
      <StockRequestModal isOpen={showRequestModal} onClose={() => setShowRequestModal(false)} />
    </>
  );
};

// Kept for any page-link items that don't belong in the nav pills section
const MobileToolsItem = ({ user, navigate }) => {
  // Items previously here (Transport, Reports, Goods In, PI Counts)
  // are now rendered as top-level nav items in the mobileMenu section
  return null;
};

// Quick Links modal pill — shown at the bottom of the mobile drawer
const MobileQuickLinksPill = () => {
  const [open, setOpen] = useState(false);
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const [linksRes, groupsRes] = await Promise.all([
          api.get(`${base}/api/quick-links`),
          api.get(`${base}/api/quick-link-groups`),
        ]);
        if (cancelled) return;
        setQuickLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
        setLinkGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      } catch (_) { /* silent — links are nice-to-have */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const getLinksForGroup = (gId) =>
    quickLinks.filter(l => (l.group_id || 'default') === gId).sort((a, b) => (a.order || 0) - (b.order || 0));

  const nonEmpty = linkGroups.filter(g => getLinksForGroup(g.id).length > 0);
  if (!nonEmpty.length) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        data-testid="mobile-nav-quick-links"
      >
        <Link2 className="w-5 h-5 flex-shrink-0" style={{ color: '#6d28d9' }} />
        <span className="font-medium text-sm" style={{ color: '#6d28d9' }}>Quick Links</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ width: '88vw', maxWidth: '400px', maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: '#6d28d9' }}>
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-white" />
                <span className="font-bold text-white">Quick Links</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-white hover:bg-white/20 rounded-full p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 52px)' }}>
              {nonEmpty.map((group, gIndex) => {
                const groupLinks = getLinksForGroup(group.id);
                return (
                  <div key={group.id} className={gIndex > 0 ? 'mt-4 pt-4 border-t border-gray-100' : ''}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: group.color || '#6d28d9' }}>{group.name}</p>
                    <div className="space-y-0.5">
                      {groupLinks.map(link => (
                        <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
                          className="flex items-center gap-2 p-2.5 rounded-lg hover:bg-purple-50 transition-colors group"
                          data-testid={`mobile-quick-link-${link.id}`}>
                          <span className="text-sm font-medium flex-1" style={{ color: group.color || '#6d28d9' }}>{link.name}</span>
                          <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-purple-600 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Cancelled Notif — flag an order number as cancelled (Admin dropdown) ────
// Self-contained (own state + API call), same pattern as GlobalUploadModal/New
// Accounts — moved here from Stock Management's sub-header so it's reachable
// from any page, gated by can_view_cancelled_notif permission.
const CancelledNotifModal = ({ isOpen, onClose }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const num = orderNumber.trim();
    if (!num) { toast.error('Enter an order number'); return; }
    setSaving(true);
    try {
      await api.post(`${API}/cancelled-tickets`, { order_number: num });
      toast.success(`Order #${num} flagged as cancelled`);
      setOrderNumber('');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-600" />
            <span className="font-bold text-gray-900">Cancelled Notif</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="cancelled-notif-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-500">Flag an order number as cancelled — Transport will warn staff if it's entered on a today/future delivery.</p>
          <input
            autoFocus
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            placeholder="Order number"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
            data-testid="cancelled-notif-order-input"
          />
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors" data-testid="cancelled-notif-cancel-btn">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
            data-testid="cancelled-notif-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            Flag Cancelled
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Admin Dropdown Pill — Price Increases + New Accounts ────────────────────────
export const AdminDropdownPill = ({ user, navigate, currentPath, hasPermission, shape = 'pill', icon: TriggerIcon = Shield }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewAccountsModal, setShowNewAccountsModal] = useState(false);
  const [showGlobalUpload, setShowGlobalUpload] = useState(false);
  const [showCancelledNotif, setShowCancelledNotif] = useState(false);
  const [showHoCalc, setShowHoCalc] = useState(false);
  // Same Zoho-login gate the Sales / CRM dropdown applies before opening Customer Management (the calculator searches CRM accounts).
  const { requireConfirmed } = useZohoSession();
  const dropdownRef = useRef(null);
  const cornerClass = shape === 'oblong' ? 'rounded-xl' : 'rounded-full';

  const canPriceIncreases = hasPermission('can_access_po_expenses');

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Custom-URL modal (opened from the embedded ToolsDropdown) is rendered via
      // createPortal to document.body, outside dropdownRef's DOM subtree — never treat
      // clicks inside it as "outside", or it gets unmounted (along with ToolsDropdown)
      // before its own click handlers (e.g. the "Open in new tab" link) can fire.
      if (e.target.closest && e.target.closest('[data-testid="custom-url-modal"]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isPriceIncActive = currentPath.startsWith('/price-increases');

  const isPOExpensesActive = currentPath.startsWith('/po-expenses');

  // Admin dropdown close handler for embedded tools
  const [adminOpen, setAdminOpen] = useState(false);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          data-testid="nav-pill-admin-dropdown"
          data-v3-open={showNewAccountsModal || showGlobalUpload || showCancelledNotif || showHoCalc ? 'true' : undefined}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDropdown(!showDropdown); setAdminOpen(!showDropdown); }}
          className={`h-9 flex items-center gap-1 lg:gap-1.5 px-2 lg:px-3 ${cornerClass} border-2 bg-white hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap ${showDropdown ? 'shadow-lg' : ''}`}
          style={{ borderColor: enconOrange, position: 'relative', zIndex: showDropdown ? 51 : 'auto' }}
        >
          <TriggerIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" style={{ color: enconOrange }} />
          <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: enconOrange }}>Admin</span>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
            style={{ color: enconOrange }}
          />
        </button>

        {showDropdown && (
          <div
            className="absolute left-1/2 -translate-x-1/2 mt-1 w-64 rounded-2xl shadow-xl overflow-visible"
            style={{
              background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
              border: `2px solid ${enconOrange}`,
              backdropFilter: 'blur(10px)',
              top: '100%',
              zIndex: 9999
            }}
          >
            <div
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
              style={{ background: 'rgba(45,45,45,0.98)', borderLeft: `2px solid ${enconOrange}`, borderTop: `2px solid ${enconOrange}` }}
            />
            <div className="relative">
              {/* ── Upload Data — always first, prominent orange pill ────── */}
              {(user?.role === 'super_admin' || user?.can_access_global_upload) && (
                <div className="p-2.5 pb-1.5 rounded-t-xl">
                  <button
                    data-testid="admin-pill-upload-data"
                    onClick={() => { setShowDropdown(false); setShowGlobalUpload(true); }}
                    className="w-full py-2.5 px-4 rounded-full flex items-center justify-center gap-2 text-white font-semibold text-sm shadow-md transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: enconOrange }}
                  >
                    <Upload className="w-4 h-4" />
                    Upload Data
                  </button>
                </div>
              )}

              {/* ── SECTION 1: Admin items (no elevated permissions needed) ── */}
              {/* BI Reports — own permission flag, independent of "Manager Admin"/Tools access */}
              {(user?.role === 'super_admin' || user?.can_access_bi_reports) && (
                <button
                  data-testid="admin-pill-bi-reports"
                  onClick={() => { setShowDropdown(false); navigate('/bi-reports'); }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                >
                  <LineChart className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">BI Reports</span>
                </button>
              )}
              {/* Price Increases */}
              {canPriceIncreases && (
                <button
                  data-testid="admin-pill-price-increases"
                  onClick={() => { setShowDropdown(false); navigate('/price-increases'); }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                >
                  <TrendingUp className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">Price Increases</span>
                </button>
              )}
              {/* Cancelled Notif — own permission flag, moved here from Stock Management sub-header */}
              {(user?.role === 'super_admin' || user?.can_view_cancelled_notif) && (
                <button
                  data-testid="admin-pill-cancelled-notif"
                  onClick={() => { setShowDropdown(false); setShowCancelledNotif(true); }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                >
                  <Ban className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">Cancelled Notif</span>
                </button>
              )}
              {/* New Accounts */}
              <button
                data-testid="admin-pill-new-accounts"
                onClick={() => { setShowDropdown(false); setShowNewAccountsModal(true); }}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
              >
                <UserPlus className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">New Accounts</span>
              </button>
              {/* H/O Support Calculator */}
              <button
                data-testid="admin-pill-ho-support-calculator"
                onClick={() => { if (!requireConfirmed()) return; setShowDropdown(false); setShowHoCalc(true); }}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
              >
                <Calculator className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">H/O Support Calculator</span>
              </button>

              {/* My Team — visible to team managers who don't have full can_access_tools */}
              {user?.is_team_manager && !user?.can_access_tools && (
                <>
                  <div className="border-t border-white/30 mt-1 pt-1 px-4 pb-1">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-orange-400">Manager</span>
                  </div>
                  <button
                    data-testid="admin-pill-my-team"
                    onClick={() => { setShowDropdown(false); navigate('/team-management'); }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-b-xl"
                  >
                    <Users2 className="w-4 h-4" style={{ color: enconOrange }} />
                    <span className="font-medium">My Team</span>
                  </button>
                </>
              )}

              {/* ── SECTION 2: Manager Admin (tools, elevated permissions) ── */}
              {user?.can_access_tools && (
                <>
                  <div className="border-t border-white/30 mt-1 pt-1 px-4 pb-1">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-orange-400">Manager Admin</span>
                  </div>
                  <ToolsDropdown
                    user={user}
                    inlineMenu={true}
                    onClose={() => setShowDropdown(false)}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Inside UI V3 the Admin windows are drawn on the page body, not inside the left bar (see InBody above). */}
      <InBody>
      {/* New Accounts Modal */}
      {showNewAccountsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowNewAccountsModal(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl flex flex-col"
            style={{ width: '95vw', height: '92vh' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-white" />
                <span className="font-bold text-white text-lg">New Accounts</span>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  title="Open form in browser tab"
                  data-testid="new-accounts-open-browser-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in Browser
                </a>
                <button onClick={() => setShowNewAccountsModal(false)} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors" data-testid="close-new-accounts-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <iframe
              src="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl"
              className="flex-1 w-full border-0 rounded-b-xl"
              title="New Accounts Form"
              allow="clipboard-write"
            />
          </div>
        </div>
      )}

      {/* Global Upload Modal — triggered by Admin > Upload Data */}
      <GlobalUploadModal
        isOpen={showGlobalUpload}
        onClose={() => setShowGlobalUpload(false)}
        user={user}
      />

      {/* H/O Support Calculator — triggered by Admin > H/O Support Calculator */}
      <HOSupportCalculatorModal
        isOpen={showHoCalc}
        onClose={() => setShowHoCalc(false)}
        user={user}
      />

      {/* Cancelled Notif Modal — triggered by Admin > Cancelled Notif */}
      <CancelledNotifModal
        isOpen={showCancelledNotif}
        onClose={() => setShowCancelledNotif(false)}
      />
      </InBody>
    </>
  );
};

// M4.5 page navigation pills - with permission checks
// inHeader=true  → renders pills only (no QuickLinksRow, no NotificationBars, no outer mb)
//                   GlobalSearch rendered inline between left/right pill groups
const M45NavPills = ({ user, mobileMenu = false, inHeader = false, isTransportOpen = false, onToggleTransport = () => {}, navUiV2 = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { returnPath } = useTransportReturn();
  const [showNewAccountsModal, setShowNewAccountsModal] = useState(false);
  const { getEffectiveBranchId } = useBranch();
  const { sectors, activeSectorId, activeSector, blueprintSector, switchSector } = useNavSectors(navUiV2 ? getEffectiveBranchId() : null);
  const blueprintRef = useRef(null);
  const [blueprintWidth, setBlueprintWidth] = useState(null);

  // Nav UI v2 — measure the branch's Default sector ("blueprint") rendered
  // width off-screen, so the visible middle slot never gets narrower than
  // it — this is what keeps the Search/Sector/Delivery/Call anchors pinned
  // at a constant position regardless of which sector is actually shown.
  useEffect(() => {
    if (!navUiV2 || !blueprintRef.current) { setBlueprintWidth(null); return; }
    const el = blueprintRef.current;
    const measure = () => setBlueprintWidth(el.scrollWidth || null);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [navUiV2, blueprintSector, user]);

  // Check permission for a page
  const hasPermission = (permissionKey) => {
    if (user?.role === 'super_admin') return true;
    return user?.[permissionKey] !== false;
  };

  const regularPages = [
    { path: '/po-expenses', label: 'P.O. Expenses', icon: ClipboardList, permissionKey: 'can_access_po_expenses' },
    // Price Increases moved to Admin dropdown pill
  ];

  const canAccessProforma = hasPermission('can_access_proforma');
  const canAccessQuotes = hasPermission('can_access_quotes');
  const filteredRegularPages = regularPages.filter(page => hasPermission(page.permissionKey));

  // ─── Mobile Nav List ─────────────────────────────────────────────────────────
  // Rendered inside the hamburger drawer on mobile; same functionality as the
  // desktop pills but presented as a compact touch-friendly vertical list.
  if (mobileMenu) {
    return (
      <div className="space-y-1" data-testid="mobile-nav-section">

        {/* ── Page links (near top, matching Hub home page nav) ─────────── */}
        {(user?.role === 'super_admin' || user?.can_access_transport) && (
          <button data-testid="mobile-nav-transport" onClick={() => navigate('/transport')}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left">
            <Truck className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
            <span className="font-medium text-sm">Transport</span>
          </button>
        )}
        {(user?.role === 'super_admin' || user?.can_access_reports) && (
          <button data-testid="mobile-nav-reports" onClick={() => navigate('/reports')}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left">
            <BarChart2 className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
            <span className="font-medium text-sm">Reports</span>
          </button>
        )}
        {(user?.role === 'super_admin' || user?.can_access_stock_management) && (
          <button data-testid="mobile-nav-stock-management" onClick={() => navigate('/stock-management')}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left">
            <Package className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
            <span className="font-medium text-sm">Stock Control</span>
          </button>
        )}
        <button data-testid="mobile-nav-goods-in" onClick={() => navigate('/goods-in')}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left">
          <PackageCheck className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
          <span className="font-medium text-sm">Goods In</span>
        </button>
        {(user?.role === 'super_admin' || user?.can_access_pi_counts) && (
          <button data-testid="mobile-nav-pi-counts" onClick={() => navigate('/pi-counts')}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left">
            <ClipboardList className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
            <span className="font-medium text-sm">PI Counts</span>
          </button>
        )}

        {/* ── Nav Pills section ──────────────────────────────── */}
        <div className="pt-2">
          <span className="text-xs text-gray-400 font-semibold px-1 uppercase tracking-wider">Navigation</span>
        </div>

        {/* Pro Forma */}
        {canAccessProforma && (
          <MobilePillGroup label="Pro Forma" icon={FileText} user={user} isQuote={false} />
        )}

        {/* Quotes */}
        {canAccessQuotes && (
          <MobilePillGroup label="Quotes" icon={FileQuestion} user={user} isQuote={true} />
        )}

        {/* Manage CRM — full LogCallDropdownPill in mobile accordion mode */}
        <LogCallDropdownPill mobileMode={true} user={user} />

        {/* Stock Transfers / Requests */}
        <MobileStockItem user={user} navigate={navigate} />

        {/* Regular pages (P.O. Expenses, Price Increases) */}
        {filteredRegularPages.map((page) => {
          const Icon = page.icon;
          const isActive = currentPath.startsWith(page.path);
          return (
            <button
              key={page.path}
              data-testid={`mobile-nav-${page.path.replace('/', '')}`}
              onClick={() => navigate(page.path)}
              className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left"
              style={isActive
                ? { background: enconOrange, color: 'white' }
                : { background: '#f9fafb' }}
            >
              <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isActive ? 'white' : enconOrange }} />
              <span className="font-medium text-sm">{page.label}</span>
            </button>
          );
        })}

        {/* New Accounts */}
        <button
          data-testid="mobile-nav-new-accounts"
          onClick={() => setShowNewAccountsModal(true)}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        >
          <UserPlus className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
          <span className="font-medium text-sm">New Accounts</span>
        </button>

        {/* Management Tools — ToolsDropdown in mobile accordion mode */}
        {user?.can_access_tools && (
          <ToolsDropdown mobileMode={true} user={user} />
        )}

        {/* ── Quick Links modal pill (bottom) ──────────────────────── */}
        <div className="pt-2 border-t">
          <MobileQuickLinksPill />
        </div>

        {/* Modals */}
        {showNewAccountsModal && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60" onClick={() => setShowNewAccountsModal(false)}>
            <div
              className="bg-white rounded-xl shadow-2xl flex flex-col"
              style={{ width: '95vw', height: '92vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-white" />
                  <span className="font-bold text-white text-lg">New Accounts</span>
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-white/20 transition-colors"
                    title="Open form in browser tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Browser
                  </a>
                  <button onClick={() => setShowNewAccountsModal(false)} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <iframe
                src="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl"
                className="flex-1 w-full border-0 rounded-b-xl"
                title="New Accounts Form"
                allow="clipboard-write"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Desktop / Tablet Pills ──────────────────────────────────────────────────────────────
  const hasAnyPage = filteredRegularPages.length > 0 || canAccessProforma || canAccessQuotes;

  // ── inHeader mode: pills + inline search (no absolute positioning) ──
  if (inHeader) {
    // ── Nav UI v2 — Sector-driven layout ──────────────────────────────────
    // Fixed far-left (Search + Sector switcher) and far-right (Delivery +
    // Call) icons; everything in between is whatever the active Sector's
    // links resolve to, permission-filtered exactly like the classic header.
    if (navUiV2) {
      const filteredLinks = (activeSector?.links || []).filter(item => !item.permission_key || hasPermission(item.permission_key));

      const renderHeaderItem = (item) => {
        switch (item.type) {
          case 'builtin_dropdown':
            switch (item.builtin_key) {
              case 'proforma':
                return <DropdownPill key={item.id} label={item.label || 'Pro Forma'} icon={FileText} isQuote={false} user={user} shape="oblong" />;
              case 'quotes':
                return <DropdownPill key={item.id} label={item.label || 'Quotes'} icon={FileQuestion} isQuote={true} user={user} shape="oblong" />;
              case 'sales_crm':
                return <LogCallDropdownPill key={item.id} user={user} shape="oblong" />;
              case 'stock_transfer_requests':
                return <StockTransfersRequestsPill key={item.id} user={user} shape="oblong" />;
              case 'admin':
                return <AdminDropdownPill key={item.id} user={user} navigate={navigate} currentPath={currentPath} hasPermission={hasPermission} shape="oblong" />;
              case 'stock_management':
                return <StockManagementDropdownPill key={item.id} user={user} shape="oblong" />;
              default:
                return null;
            }
          case 'page':
          case 'custom_link':
            return <CustomLinkPill key={item.id} item={item} navigate={navigate} />;
          case 'custom_dropdown':
            return <CustomDropdownPill key={item.id} item={item} navigate={navigate} />;
          default:
            return null;
        }
      };

      // Lightweight, non-interactive stand-ins for width measurement only —
      // avoids mounting duplicate live dropdowns (with their own state/
      // listeners) off-screen just to read a width.
      const renderMeasurementItem = (item) => (
        <div key={item.id} className="h-9 flex items-center gap-1.5 px-2 lg:px-3 xl:px-6 rounded-xl border-2 whitespace-nowrap">
          <span className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4 inline-block" />
          <span className="font-semibold text-[11px] lg:text-xs xl:text-sm">{item.label}</span>
          {item.type !== 'custom_link' && item.type !== 'page' && <span className="w-4 h-4 inline-block" />}
        </div>
      );

      const blueprintLinks = (blueprintSector?.links || []).filter(item => !item.permission_key || hasPermission(item.permission_key));

      return (
        <div className="flex items-center flex-nowrap gap-1.5" data-testid="nav-header-v2-row">
          {/* ── Far left (fixed): Sector switcher — search now lives in the sub-header ── */}
          <div className="flex items-center gap-1.5 shrink-0">
            <SectorDropdown sectors={sectors} activeSectorId={activeSectorId} onChange={switchSector} />
          </div>

          {/* ── Middle: active Sector's own links. min-width is reserved from the
              Default sector's ("blueprint") own rendered width, so the anchors
              either side never move — the slot only ever grows wider than the
              blueprint if the active sector genuinely has more links than it. */}
          <div
            className="flex items-center justify-evenly gap-1.5 flex-nowrap px-1"
            style={{ minWidth: blueprintWidth ? `${blueprintWidth}px` : undefined }}
          >
            {filteredLinks.map(renderHeaderItem)}
          </div>

          {/* Off-screen — exists only so blueprintRef can measure the Default sector's width */}
          <div
            ref={blueprintRef}
            aria-hidden="true"
            className="flex items-center gap-1.5"
            style={{ position: 'fixed', top: -9999, left: -9999, visibility: 'hidden', pointerEvents: 'none' }}
          >
            {blueprintLinks.map(renderMeasurementItem)}
          </div>

          {/* ── Far right (fixed): Delivery + Call ──
              While on /transport, this icon temporarily becomes a "back to
              <last page>" icon instead of navigating to Transport again —
              reverts to the normal Transport icon the moment the user leaves
              Transport by any means (see TransportReturnContext). */}
          <div className="flex items-center gap-1.5 shrink-0">
            {(user?.role === 'super_admin' || user?.can_access_transport) && (() => {
              const onTransport = currentPath.startsWith('/transport');
              if (onTransport && returnPath) {
                const page = returnPath === '/'
                  ? { label: 'Home', icon: 'Home' }
                  : [...PAGE_REGISTRY].filter(p => p.route !== '/' && returnPath.startsWith(p.route))
                      .sort((a, b) => b.route.length - a.route.length)[0];
                const ReturnIcon = (page && ICON_MAP[page.icon]) || Home;
                const label = page?.label || 'Home';
                return (
                  <button
                    data-testid="header-delivery-icon-btn"
                    onClick={() => navigate(returnPath)}
                    className="hidden md:flex p-2.5 md:p-3 rounded-xl transition-all duration-200 items-center hover:shadow-lg hover:scale-105"
                    style={{ background: 'white', boxShadow: '0 2px 10px rgba(251, 146, 60, 0.25)' }}
                    title={`Back to ${label}`}
                  >
                    <ReturnIcon className="w-6 h-6 md:w-7 md:h-7" style={{ color: enconOrange }} strokeWidth={2.25} />
                  </button>
                );
              }
              return (
                <button
                  data-testid="header-delivery-icon-btn"
                  onClick={() => navigate('/transport')}
                  className="hidden md:flex p-2.5 md:p-3 rounded-xl transition-all duration-200 items-center hover:shadow-lg hover:scale-105"
                  style={{ background: 'white', boxShadow: '0 2px 10px rgba(251, 146, 60, 0.25)' }}
                  title="Transport"
                >
                  <Truck className="w-6 h-6 md:w-7 md:h-7" style={{ color: enconOrange }} strokeWidth={2.25} />
                </button>
              );
            })()}
            <QuickCallNoteButton />
          </div>
        </div>
      );
    }

    const pillClass = "h-9 flex items-center gap-1 lg:gap-1.5 px-2 lg:px-3 rounded-full border-2 transition-all duration-200 whitespace-nowrap hover:shadow-md hover:scale-105 bg-white";

    return (
      <>
        {/* Three-column layout: LEFT pills | Search (centered) | RIGHT pills */}
        <div className="flex items-center flex-nowrap w-full gap-1 lg:gap-1.5">

          {/* ── LEFT group (3 pills, right-aligned so search stays centred) ── */}
          <div className="flex items-center gap-1 lg:gap-1.5 flex-1 justify-end flex-nowrap">
            {canAccessProforma && (
              <DropdownPill label="Pro Forma" icon={FileText} permissionKey="can_access_proforma" isQuote={false} user={user} />
            )}
            {canAccessQuotes && (
              <DropdownPill label="Quotes" icon={FileQuestion} permissionKey="can_access_quotes" isQuote={true} user={user} />
            )}
            <LogCallDropdownPill user={user} />
          </div>

          {/* ── CENTER: Call icon + Transport icon (search now lives in the sub-header) ── */}
          <div className="shrink-0 px-1 lg:px-2 flex items-center gap-1.5 lg:gap-2" data-testid="header-search-center">
            <QuickCallNoteButton />
            {(user?.role === 'super_admin' || user?.can_access_transport) && (
              <button
                data-testid="header-transport-icon-btn"
                onClick={onToggleTransport}
                className={`hidden md:flex p-2.5 md:p-3 rounded-xl transition-all duration-200 items-center gap-1 ${
                  isTransportOpen ? 'ring-2 ring-orange-300' : 'hover:shadow-lg hover:scale-105'
                }`}
                style={{ background: 'white', boxShadow: '0 2px 10px rgba(251, 146, 60, 0.25)' }}
                title="Transport"
              >
                <Truck className="w-6 h-6 md:w-7 md:h-7" style={{ color: enconOrange }} strokeWidth={2.25} />
                <ChevronDown className={`w-4 h-4 transition-transform ${isTransportOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
              </button>
            )}
          </div>

          {/* ── RIGHT group (3 pills: Stock Trf/Req | P.O. Expenses | Admin) ── */}
          <div className="flex items-center gap-1 lg:gap-1.5 flex-1 justify-start flex-nowrap">
            <StockTransfersRequestsPill user={user} />

            {/* P.O. Expenses — its own pill */}
            {hasPermission('can_access_po_expenses') && (
              <button
                data-testid="nav-pill-po-expenses"
                onClick={() => navigate('/po-expenses')}
                className={`h-9 flex items-center gap-1 lg:gap-1.5 px-2 lg:px-3 rounded-full border-2 transition-all duration-200 whitespace-nowrap hover:shadow-md hover:scale-105 bg-white`}
                style={{
                  borderColor: currentPath.startsWith('/po-expenses') ? enconPurple : enconOrange,
                  backgroundColor: currentPath.startsWith('/po-expenses') ? enconPurple : 'white',
                }}
              >
                <ClipboardList
                  className="w-3 h-3 lg:w-3.5 lg:h-3.5"
                  style={{ color: currentPath.startsWith('/po-expenses') ? 'white' : enconOrange }}
                />
                <span
                  className="font-semibold text-[11px] lg:text-xs xl:text-sm"
                  style={{ color: currentPath.startsWith('/po-expenses') ? 'white' : enconOrange }}
                >
                  P.O. Expenses
                </span>
              </button>
            )}

            {/* Admin dropdown: Admin items + Manager Admin section */}
            <AdminDropdownPill
              user={user}
              navigate={navigate}
              currentPath={currentPath}
              hasPermission={hasPermission}
            />
          </div>
        </div>
      </>
    );
  }

  if (!hasAnyPage) {
    return (
      <div className="hidden md:flex justify-center mb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 rounded-full border-2 hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap"
          style={{ backgroundColor: enconOrange, borderColor: enconOrange }}
        >
          <Home className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4 text-white" />
          <span className="font-semibold text-[11px] lg:text-xs xl:text-sm text-white">Home</span>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Purple quick-link strip (EBiS | Encon.co.uk | Price Changes | Phocas etc.) */}
      <QuickLinksRow />

      {/* Pills — hidden on mobile (shown in the hamburger drawer instead) */}
      <div className="hidden md:flex justify-center mb-6 px-1 md:px-2 lg:px-4 xl:px-8">
        <div className="flex flex-wrap md:flex-nowrap justify-center items-center gap-1 lg:gap-2 xl:gap-3 w-full">
          {/* Pro Forma Dropdown */}
          {canAccessProforma && (
            <DropdownPill 
              label="Pro Forma" 
              icon={FileText} 
              permissionKey="can_access_proforma"
              isQuote={false}
              user={user}
            />
          )}

          {/* Quotes Dropdown */}
          {canAccessQuotes && (
            <DropdownPill 
              label="Quotes" 
              icon={FileQuestion} 
              permissionKey="can_access_quotes"
              isQuote={true}
              user={user}
            />
          )}

          {/* Log CRM Call dropdown */}
          <LogCallDropdownPill user={user} />

          {/* Combined Stock Trf/Req pill */}
          <StockTransfersRequestsPill user={user} />

          {/* Regular Navigation Pills */}
          {filteredRegularPages.map((page) => {
            const Icon = page.icon;
            const isCurrentPage = currentPath.startsWith(page.path);
            return (
              <button
                key={page.path}
                data-testid={`nav-pill-${page.path.replace('/', '')}`}
                onClick={() => !isCurrentPage && navigate(page.path)}
                className={`flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 rounded-full border-2 transition-all duration-200 whitespace-nowrap ${
                  isCurrentPage ? 'cursor-default' : 'bg-white hover:shadow-lg hover:scale-105'
                }`}
                style={{
                  borderColor: isCurrentPage ? enconPurple : enconOrange,
                  backgroundColor: isCurrentPage ? enconPurple : 'white'
                }}
              >
                <Icon className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: isCurrentPage ? 'white' : enconOrange }} />
                <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: isCurrentPage ? 'white' : enconOrange }}>
                  {page.label}
                </span>
              </button>
            );
          })}

          {/* New Accounts Pill */}
          <button
            data-testid="nav-pill-new-accounts"
            onClick={() => setShowNewAccountsModal(true)}
            className="flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 rounded-full border-2 bg-white hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap"
            style={{ borderColor: enconOrange }}
          >
            <UserPlus className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: enconOrange }} />
            <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: enconOrange }}>New Accounts</span>
          </button>

          {/* Tools Dropdown - Permission gated */}
          {user?.can_access_tools && (
            <ToolsDropdown user={user} />
          )}
        </div>
      </div>

      {/* Notification bars */}
      {currentPath !== '/manage-calls' && (
        <div className="px-4 md:px-8">
          <NotificationBars user={user} />
        </div>
      )}

      {/* New Accounts Modal */}
      {showNewAccountsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowNewAccountsModal(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl flex flex-col"
            style={{ width: '95vw', height: '92vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-white" />
                <span className="font-bold text-white text-lg">New Accounts</span>
              </div>
              <button
                onClick={() => setShowNewAccountsModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                data-testid="close-new-accounts-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              src="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl"
              className="flex-1 w-full border-0 rounded-b-xl"
              title="New Accounts Form"
              allow="clipboard-write"
            />
          </div>
        </div>
      )}
    </>
  );
};

// ─── UI V3 — a single item taken out of one of the drop-downs, as a link of its own ─────────────────────────────────────────────────
// (Settings > User Interface > User Spaces > Links > Add Link > Single link; the list of them is BUILTIN_ACTIONS in utils/navHeaderRegistry.js.) It does exactly what the same
// item does inside its drop-down: the same window, page or form, and the same checks on who may see it. The Sales / CRM ones are done by LogCallDropdownPill itself.
const ACTION_BY_KEY = Object.fromEntries(BUILTIN_ACTIONS.map((action) => [action.key, action]));
const NEW_ACCOUNTS_FORM = 'https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl';
const ACTION_ROUTES = {
  stock_transfers: '/stock-transfer',
  stockctl_ordering: '/stock-management?view=stock-ordering',
  stockctl_efficiency: '/stock-management?view=stock-efficiency',
  stockctl_stocktake: '/stock-management?view=stocktake',
  admin_bi_reports: '/bi-reports',
  admin_price_increases: '/price-increases',
  admin_my_team: '/team-management',
};
const ACTION_WINDOWS = {
  quotes_create: ['quote', 'create'], quotes_recent: ['quote', 'recent'], quotes_upload: ['quote', 'upload'],
  proforma_create: ['proforma', 'create'], proforma_recent: ['proforma', 'recent'], proforma_upload: ['proforma', 'upload'],
  stock_b29: ['b29'], stock_request: ['stockrequest'], admin_cancelled_notif: ['cancelled'], admin_new_accounts: ['newaccounts'], admin_ho_calculator: ['hocalc'],
};

export const NavActionLink = ({ item, user, navigate }) => {
  const { requireConfirmed } = useZohoSession();
  const [win, setWin] = useState(null); // [kind, mode?] of the window that is open
  const def = ACTION_BY_KEY[item.builtin_key];
  if (!def) return null;
  const IconComp = ICON_MAP[item.icon] || ICON_MAP[def.icon] || Link2;
  const label = item.label || def.label;
  // Sales / CRM items are done by the CRM component, so its access check and the chaining between its windows work as they do inside the drop-down.
  if (def.group === 'crm') return <LogCallDropdownPill user={user} shape="oblong" action={def.key} actionLabel={label} icon={IconComp} />;
  if (!actionVisible(def, user)) return null;

  const run = () => {
    if (ACTION_ROUTES[def.key]) { navigate(ACTION_ROUTES[def.key]); return; }
    if (def.key === 'admin_ho_calculator' && !requireConfirmed()) return;
    if (ACTION_WINDOWS[def.key]) setWin(ACTION_WINDOWS[def.key]);
  };
  const close = () => setWin(null);
  const kind = win ? win[0] : null;
  return (
    <>
      <button
        type="button"
        data-testid={`nav-action-${def.key}`}
        data-v3-open={win ? 'true' : undefined}
        onClick={run}
        className="h-9 flex items-center gap-1.5 px-3 rounded-xl border-2 bg-white hover:shadow-md transition-all duration-200 hover:scale-105 whitespace-nowrap"
        style={{ borderColor: enconOrange }}
      >
        <IconComp className="w-3.5 h-3.5" style={{ color: enconOrange }} />
        <span className="font-semibold text-[11px] lg:text-xs" style={{ color: enconOrange }}>{label}</span>
      </button>
      {kind === 'quote' && <QuotesModalInBody isOpen onClose={close} mode={win[1]} user={user} />}
      {kind === 'proforma' && <ProformaModalInBody isOpen onClose={close} mode={win[1]} user={user} />}
      <InBody>
        {kind === 'stockrequest' && <StockRequestModal isOpen onClose={close} />}
        {kind === 'b29' && <B29StockTransfer.Modal isOpen onClose={close} user={user} />}
        {kind === 'hocalc' && <HOSupportCalculatorModal isOpen onClose={close} user={user} />}
        {kind === 'cancelled' && <CancelledNotifModal isOpen onClose={close} />}
        {kind === 'newaccounts' && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={close}>
            <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: '95vw', height: '92vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
                <div className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-white" /><span className="font-bold text-white text-lg">New Accounts</span></div>
                <div className="flex items-center gap-1">
                  <a href={NEW_ACCOUNTS_FORM} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Open form in browser tab"><ExternalLink className="w-4 h-4" />Open in Browser</a>
                  <button onClick={close} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors" data-testid="close-new-accounts-action-modal"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <iframe src={NEW_ACCOUNTS_FORM} className="flex-1 w-full border-0 rounded-b-xl" title="New Accounts Form" allow="clipboard-write" />
            </div>
          </div>
        )}
      </InBody>
    </>
  );
};

// ─── UI V3 — the navigation tabs for the V3 left bar ──────────────────────────────────────────────────────────
// The same items the Global Header shows (the person's Sector links when Nav UI v2 is on, otherwise the classic six pills),
// drawn one under another, followed by Transport and the quick call note button the header has on its far right.
// Same components, same permission checks, same modals: only the layout and the skin change.
export const NavSectorItems = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { navUiV2: navUiV2Preview } = useUIPreview();
  // A Space is a side bar setup (a Sector), so once the new home page is on for this person the side bar always works from their Space.
  const { ogHome } = useDashboardV3();
  const navUiV2 = navUiV2Preview || !ogHome;
  const { getEffectiveBranchId } = useBranch();
  const { sectors, activeSectorId, activeSector, switchSector } = useNavSectors(navUiV2 ? getEffectiveBranchId() : null);

  const hasPermission = (permissionKey) => {
    if (user?.role === 'super_admin') return true;
    return user?.[permissionKey] !== false;
  };
  const canSeeTransport = user?.role === 'super_admin' || !!user?.can_access_transport;

  // Icons and colours chosen in Settings > Side Bars. They belong to the link itself, so a link looks the same in every sector and for every person.
  const { styles: linkStyles } = useNavLinkStyles();
  const { activeActivity } = useActivityBar();
  // A window reopened from Recents / Open is drawn by the shell, not by its tab, so the tab cannot mark it open: its link is found from the kind of window.
  const hostLinkIds = activeActivity && activeActivity.renderedByHost ? (HOST_WINDOW_LINKS[activeActivity.type] || []) : [];
  const iconFor = (item, fallback) => (linkStyles[linkStyleKey(item)]?.icon && ICON_MAP[linkStyles[linkStyleKey(item)].icon]) || fallback;
  const withIcon = (item) => {
    const chosen = linkStyles[linkStyleKey(item)]?.icon;
    return chosen ? { ...item, icon: chosen } : item;
  };
  const TransportIcon = iconFor(TRANSPORT_LINK, Truck);

  const renderItem = (item) => {
    switch (item.type) {
      case 'builtin_dropdown':
        switch (item.builtin_key) {
          case 'proforma':
            return <DropdownPill key={item.id} label={item.label || 'Pro Forma'} icon={iconFor(item, FileText)} isQuote={false} user={user} shape="oblong" />;
          case 'quotes':
            return <DropdownPill key={item.id} label={item.label || 'Quotes'} icon={iconFor(item, FileQuestion)} isQuote={true} user={user} shape="oblong" />;
          case 'sales_crm':
            return <LogCallDropdownPill key={item.id} user={user} shape="oblong" icon={iconFor(item, null)} />;
          case 'stock_transfer_requests':
            return <StockTransfersRequestsPill key={item.id} user={user} shape="oblong" icon={iconFor(item, Package)} />;
          case 'admin':
            return <AdminDropdownPill key={item.id} user={user} navigate={navigate} currentPath={currentPath} hasPermission={hasPermission} shape="oblong" icon={iconFor(item, Shield)} />;
          case 'stock_management':
            return <StockManagementDropdownPill key={item.id} user={user} shape="oblong" icon={iconFor(item, Boxes)} />;
          default:
            return null;
        }
      case 'page':
      case 'custom_link':
        return <CustomLinkPill key={item.id} item={withIcon(item)} navigate={navigate} />;
      case 'custom_dropdown':
        return <CustomDropdownPill key={item.id} item={withIcon(item)} navigate={navigate} />;
      case 'builtin_action':
        return <NavActionLink key={item.id} item={withIcon(item)} user={user} navigate={navigate} />;
      default:
        return null;
    }
  };

  const transportButton = canSeeTransport ? (
    <button
      key="v3-transport"
      type="button"
      data-testid="v3-nav-transport"
      onClick={() => navigate('/transport')}
      className="h-9 flex items-center gap-1.5 px-3 rounded-xl border-2 bg-white whitespace-nowrap"
      style={{ borderColor: enconOrange }}
    >
      <TransportIcon className="w-3.5 h-3.5" style={{ color: enconOrange }} />
      <span className="font-semibold text-[11px] lg:text-xs" style={{ color: enconOrange }}>Transport</span>
    </button>
  ) : null;

  if (navUiV2) {
    const items = (activeSector?.links || []).filter((item) => !item.permission_key || hasPermission(item.permission_key));
    // Transport is the button at the top of the list, so a Transport page link in the sector is not shown a second time. The list keeps its order: the page being
    // viewed stays where it is, with an orange ring. While a window is open its own link has the orange ring and the page behind it has a purple one.
    const listItems = items.filter((item) => linkStyleKey(item) !== linkStyleKey(TRANSPORT_LINK));
    const onTransport = currentPath.startsWith('/transport');
    const activeItem = onTransport ? null : listItems.find((item) => item.type === 'page' && item.route && item.route !== '/' && currentPath.startsWith(item.route));
    const linkCss = [linkColorCss([...items, TRANSPORT_LINK], linkStyles), linkActiveCss(onTransport ? TRANSPORT_LINK : activeItem, hostLinkIds.length > 0), linkOpenCss(hostLinkIds)].filter(Boolean).join('\n');
    return (
      <div className="v3-actions" data-testid="v3-nav-sector-items">
        {linkCss && <style>{linkCss}</style>}
        <SectorDropdown sectors={sectors} activeSectorId={activeSectorId} onChange={switchSector} />
        {transportButton}
        {listItems.map(renderItem)}
      </div>
    );
  }

  const onPoExpenses = currentPath.startsWith('/po-expenses');
  return (
    <div className="v3-actions" data-testid="v3-nav-classic-items">
      <style>{[linkColorCss([...CLASSIC_LINKS, TRANSPORT_LINK], linkStyles), linkActiveCss(currentPath.startsWith('/transport') ? TRANSPORT_LINK : (onPoExpenses ? ['nav-pill-po-expenses'] : null), hostLinkIds.length > 0), linkOpenCss(hostLinkIds)].filter(Boolean).join('\n')}</style>
      {transportButton}
      {hasPermission('can_access_proforma') && (
        <DropdownPill label="Pro Forma" icon={FileText} permissionKey="can_access_proforma" isQuote={false} user={user} shape="oblong" />
      )}
      {hasPermission('can_access_quotes') && (
        <DropdownPill label="Quotes" icon={FileQuestion} permissionKey="can_access_quotes" isQuote={true} user={user} shape="oblong" />
      )}
      <LogCallDropdownPill user={user} shape="oblong" />
      <StockTransfersRequestsPill user={user} shape="oblong" />
      {hasPermission('can_access_po_expenses') && (
        <button
          data-testid="nav-pill-po-expenses"
          onClick={() => navigate('/po-expenses')}
          className="h-9 flex items-center gap-1 lg:gap-1.5 px-2 lg:px-3 rounded-xl border-2 transition-all duration-200 whitespace-nowrap bg-white"
          style={{ borderColor: enconOrange, backgroundColor: 'white' }}
        >
          <ClipboardList className="w-3 h-3 lg:w-3.5 lg:h-3.5" style={{ color: enconOrange }} />
          <span className="font-semibold text-[11px] lg:text-xs xl:text-sm" style={{ color: enconOrange }}>P.O. Expenses</span>
        </button>
      )}
      <AdminDropdownPill user={user} navigate={navigate} currentPath={currentPath} hasPermission={hasPermission} shape="oblong" />
    </div>
  );
};

// UI V3 — the same navigation tabs as icons, for the left bar when it is collapsed to its icon strip. A tab that is a plain link
// goes straight there; a tab that is a dropdown (Pro Forma, Quotes, Sales / CRM, Stock Trf/Req, Admin, custom dropdowns) calls
// onOpenMenu, which opens the full bar so the real dropdown can be used.
export const NavSectorStripItems = ({ user, onOpenMenu }) => {
  const navigate = useNavigate();
  const { navUiV2: navUiV2Preview } = useUIPreview();
  const { ogHome } = useDashboardV3();
  const navUiV2 = navUiV2Preview || !ogHome;
  const { getEffectiveBranchId } = useBranch();
  const { activeSector } = useNavSectors(navUiV2 ? getEffectiveBranchId() : null);

  const hasPermission = (permissionKey) => {
    if (user?.role === 'super_admin') return true;
    return user?.[permissionKey] !== false;
  };

  let entries;
  if (navUiV2) {
    entries = (activeSector?.links || [])
      .filter((item) => !item.permission_key || hasPermission(item.permission_key))
      .map((item) => ({
        id: item.id,
        label: item.label,
        Icon: (item.icon && ICON_MAP[item.icon]) || Link2,
        kind: (item.type === 'page' || item.type === 'custom_link') ? 'link' : 'menu',
        route: item.route,
        url: item.url,
      }));
  } else {
    entries = [
      hasPermission('can_access_proforma') && { id: 'proforma', label: 'Pro Forma', Icon: FileText, kind: 'menu' },
      hasPermission('can_access_quotes') && { id: 'quotes', label: 'Quotes', Icon: FileQuestion, kind: 'menu' },
      { id: 'sales_crm', label: 'Sales / CRM', Icon: Phone, kind: 'menu' },
      { id: 'stock', label: 'Stock Trf/Req', Icon: Package, kind: 'menu' },
      hasPermission('can_access_po_expenses') && { id: 'po-expenses', label: 'P.O. Expenses', Icon: ClipboardList, kind: 'link', route: '/po-expenses' },
      { id: 'admin', label: 'Admin', Icon: Shield, kind: 'menu' },
    ].filter(Boolean);
  }
  if (user?.role === 'super_admin' || user?.can_access_transport) {
    entries.push({ id: 'transport', label: 'Transport', Icon: Truck, kind: 'link', route: '/transport' });
  }

  const go = (entry) => {
    if (entry.kind === 'menu') { onOpenMenu(); return; }
    if (entry.url) { window.open(entry.url, '_blank', 'noopener'); return; }
    navigate(entry.route || '/');
  };

  return entries.map((entry) => (
    <button
      key={entry.id}
      type="button"
      className="v3-nav-item"
      onClick={() => go(entry)}
      title={entry.label}
      aria-label={entry.label}
      data-testid={`v3-strip-${entry.id}`}
    >
      <span className="v3-nav-chip"><entry.Icon className="v3-i" /></span>
    </button>
  ));
};

export default M45NavPills;
